import { useModel } from "@stem-sims/nexus"
import { useEffect, useState, useRef } from "react"
import { Button, Col, Row } from "react-bootstrap"
import { useLocation } from "react-router-dom"
import { toast } from "react-toastify"
import { ClassResponse } from "shared/types/studentTypes"
import StudentCoreIdeaDetails from "./StudentCoreIdeaDetails"
import { Form } from "react-bootstrap"
import styles from "./styles.module.scss"
import { studentReviewAssignments } from "shared/routes/science35"
import EmptyTablePreview from "components/Dashboards/General/EmptyTablePreview"
import SelectGradeLevelDropdown from "components/Dashboards/General/Science35/SelectGradeLevelDropdown"
import { ReviewGradeType } from "shared/types/science35Types"

import StudentCoreIdeaCard from "./StudentCoreIdeaCard"

function StudentJeopardy({ activeClass }: { activeClass: ClassResponse }) {
    const location = useLocation()

    const { assignmentId } = location.state as { assignmentId?: string } || {}

    const [selectedAssignmentId, setSelectedAssignmentId] = useState<string>(null)
    const [selectedGrades, setSelectedGrades] = useState<ReviewGradeType[]>([])
    const [hideCompleted, setHideCompleted] = useState<boolean>(false)
    const coreIdeaListRef = useRef(null)

    const { response: data, loading } = useModel({
        model: studentReviewAssignments,
        onError: (err) => (toast.error(err.response?.data?.message ?? "There has been an error loading the assignments. Please try again.")),
        props: { classId: activeClass?.id, gradeLevels: selectedGrades, hideCompleted: hideCompleted }
    })

    useEffect(() => {
        if (!assignmentId || !data) return
        setSelectedAssignmentId(assignmentId)
    }, [assignmentId, data])

    useEffect(() => {
        if (!selectedAssignmentId) return
        if (coreIdeaListRef.current) {
            coreIdeaListRef.current.scrollTop = 0
        }
    }, [selectedAssignmentId])

    if (loading || !data) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    const reviewAssignments = data.reviewAssignments
    const lastCompletedAssignment = data.lastCompletedReviewAssignment
    const filteredReviewAssignments = reviewAssignments.filter(a => a.assignment.id !== lastCompletedAssignment?.assignment.id)
    const allCoreIdeasCount = data.allCoreIdeasCount
    const filterGrades = data.availableFilterGrades

    if (reviewAssignments.length === 0) {
        return (
            <>
                <h1 className="my-4"><i className="fa-solid fa-circle-question"></i> Core Ideas</h1>
                {allCoreIdeasCount > 0 && 
                    <div>
                        <Form className="text-start text-primary fw-bold ms-4 my-3">
                            <Form.Check
                                role="button"
                                onChange={() => setHideCompleted(!hideCompleted)}
                                type="switch"
                                checked={!hideCompleted}
                                label={<span role="button" onClick={() => setHideCompleted(!hideCompleted)}>Show All <span className="d-none d-sm-inline">Core Ideas</span></span>}
                            />
                        </Form>
                        {filterGrades.length > 1 &&
                            <div className="my-3 ms-4">
                                <SelectGradeLevelDropdown grades={filterGrades} selectedGrades={selectedGrades} onSelect={(grades) => setSelectedGrades(grades)} />
                            </div>}
                        {data.completedCoreIdeasCount === allCoreIdeasCount && 
                            <h4 className="text-center text-success fw-bold my-5">
                                <i className="fa-solid fa-gift me-2" />
                                Congratulations! You've mastered all the core ideas for your grade level.
                            </h4>}
                    </div>}
                {allCoreIdeasCount === 0 && <EmptyTablePreview title="Core Ideas" description="Your teacher hasn't added core ideas yet." />}
            </>
        )
    }

    const isSwapped = selectedAssignmentId !== null

    return (
        <div>
            <h1 className="my-4"><i className="fa-solid fa-circle-question"></i> Core Ideas</h1>
            <div className="d-flex justify-content-between align-items-center">
                {isSwapped && <Button
                    variant="dark"
                    className="d-flex align-items-center mx-4 my-3"
                    onClick={() => setSelectedAssignmentId(null)}
                >
                    <i className="fas fa-arrow-left me-1" /> Return to Board View
                </Button>}
                <Form className="text-start text-primary fw-bold mx-4 my-3">
                    <Form.Check
                        role="button"
                        onChange={() => setHideCompleted(!hideCompleted)}
                        type="switch"
                        checked={!hideCompleted}
                        label={<span role="button" onClick={() => setHideCompleted(!hideCompleted)}>Show All <span className="d-none d-sm-inline">Core Ideas</span></span>}
                    />
                </Form>
            </div>
            <Row className="mx-1 mb-0">
                <Col lg={isSwapped ? 3 : 9} className={`${isSwapped? "" : styles.cardsCol} ${isSwapped ? "order-2" : "order-1"}`}>
                    <div ref={coreIdeaListRef} className={`${styles.coreIdeasContainer} container-fluid d-flex flex-wrap gap-3 pb-3 ${isSwapped ? "px-3 justify-content-end" : ""}`}>
                        {lastCompletedAssignment &&
                            <StudentCoreIdeaCard
                                index={1}
                                assignment={lastCompletedAssignment}
                                onSelect={assignmentId => {
                                    setSelectedAssignmentId(selectedAssignmentId === assignmentId ? null : assignmentId)
                                }}
                                selected={selectedAssignmentId === lastCompletedAssignment?.assignment.id}
                                isLastCompleted={true}
                            />}
                        {filteredReviewAssignments.sort((a,b) =>{
                            if (a.assignment.id === selectedAssignmentId) return -1
                            if (b.assignment.id === selectedAssignmentId) return 1
                            return 0
                        }).map((assignment, index) =>
                            <StudentCoreIdeaCard
                                key={assignment.assignment.id}
                                index={index}
                                assignment={assignment}
                                onSelect={assignmentId => {
                                    setSelectedAssignmentId(selectedAssignmentId === assignmentId ? null : assignmentId)
                                }}
                                selected={selectedAssignmentId === assignment.assignment.id}
                                isLastCompleted={false}
                            />)}
                    </div>
                </Col>
                <Col lg={isSwapped ? 9 : 3} className={`${isSwapped? `${styles.detailsCol} order-1` : "order-2"} d-flex justify-content-center px-3`}>
                    <StudentCoreIdeaDetails reviewAssignment={lastCompletedAssignment?.assignment.id === selectedAssignmentId ? lastCompletedAssignment : reviewAssignments.find(a => a.assignment.id === selectedAssignmentId)} />
                </Col>
            </Row>
        </div>
    )
}

export default StudentJeopardy
